import { useState, useEffect, useContext } from 'react';
import FreeDrawCanvas from '../FreeDrawCanvas.js';
import { ColorProvider, ColorContext } from '../Context/ColorContext';
import { BackendContext } from '../Context/BackendContext';
import Chat from '../Components/Chat.js';
import { useNavigate, useParams } from 'react-router-dom';
import { Button } from '@heroui/react';
import { ArrowRight, Sparkles } from 'lucide-react';

function FreeDrawPage() {
  return (
    <div className="">
      <ColorProvider>
        <Content />
      </ColorProvider>
    </div>
  );
}

function Content() {
  const { pageId } = useParams();
  const page = pageId || 'A';
  const navigate = useNavigate();
  const backendContext = useContext(BackendContext);
  const colorContext = useContext(ColorContext);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [artworkPromptOpen, setArtworkPromptOpen] = useState(false);

  useEffect(() => {
    let alive = true;

    async function fetchCanvas() {
      try {
        setLoadError(null);
        await Promise.all([
          Promise.resolve(backendContext.fetchCanvas(page)),
          Promise.resolve(backendContext.fetchChunks(page)),
        ]);
      } catch (error) {
        console.error('Failed to load FreeDraw canvas:', error);
        if (alive) {
          setLoadError('Unable to load the free draw canvas right now.');
        }
      } finally {
        if (alive) {
          setIsLoading(false);
        }
      }
    }

    fetchCanvas();
    return () => {
      alive = false;
    };
    }, [page]);

  if (isLoading) {
    return (
      <div className="flex h-screen w-screen items-center justify-center bg-slate-900 text-white">
        Checking page...
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="flex h-screen w-screen items-center justify-center bg-slate-900 px-4 text-center text-white">
        {loadError}
      </div>
    );
  }

  return (
    <div className="h-screen w-screen overflow-hidden bg-slate-900">
      <Chat currentPage={page} />
      <div className="pointer-events-none fixed top-[78px] left-1/2 z-[950] -translate-x-1/2">
        <div className="rounded-full border border-slate-700 bg-slate-950/90 px-5 py-2 text-sm font-semibold tracking-[0.2em] text-white shadow-xl backdrop-blur">
          {page}
        </div>
      </div>
      <div className="fixed top-[122px] left-1 z-[940]">
        <div
          onClick={() => setArtworkPromptOpen((prev) => !prev)}
          className="w-12 h-12 bg-gray-800 text-white rounded-r-md shadow-md flex items-center justify-center cursor-pointer hover:bg-gray-700 border border-slate-700"
        >
          <Sparkles size={20} className="text-indigo-300" />
        </div>
        {artworkPromptOpen && (
          <div className="mt-3 ml-1 max-w-[280px] rounded-2xl border border-slate-700/80 bg-slate-950/90 p-4 text-white shadow-xl backdrop-blur">
            <div className="flex items-start gap-3">
              <div className="mt-0.5 rounded-xl border border-indigo-500/30 bg-indigo-500/10 p-2 text-indigo-300">
                <Sparkles size={18} />
              </div>
              <div className="min-w-0">
                <div className="text-sm font-semibold">Want more control?</div>
                <p className="mt-1 text-xs leading-5 text-slate-300">
                  If you like placing pixels here, try the artwork canvas for page {page} and build untouchable pieces.
                </p>
                <Button
                  onPress={() => navigate(`/Canvas/${encodeURIComponent(page)}`)}
                  className="mt-3 w-full bg-slate-100 text-slate-950 hover:bg-white"
                  endContent={<ArrowRight size={16} />}
                >
                  Try Artwork Canvas
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
      <div className="page-price pt-16 flex w-full">
        <div className="absolute top-[32px] right-0 bottom-0 left-0 flex flex-col">
          <FreeDrawCanvas
            page={page}
            selectedColor={colorContext.selectedColor}
            setSelectedColor={colorContext.setSelectedColor}
            backendContext={backendContext}
          />
        </div>
        <div className="absolute top-12 left-1 text-white px-3 py-1 rounded text-sm font-mono z-[1000]" />
      </div>
    </div>
  );
}

export default FreeDrawPage;

